/**
 * MembershipsHomePage — the My Impact SECTION HOME (`/memberships`).
 *
 * DEV-1458 / POLICY_NAVIGATION_RAILS §7.7 ruling 4 (Skip, 2026-07-12).
 *
 * My Impact never had a home route of its own: the LeftNav item pre-resolved
 * to a community URL (the DEV-1448 resolver) and the section BORROWED community
 * pages as its main pane — which is why the rail had to claim `/communities/*`
 * to persist, which is why the Discover Globe delivered you to a community page
 * wrapped in your personal chrome. This route is the structural fix. The rail
 * renders beside it; the main pane is the section's own content.
 *
 *   POST-REVEAL → the full signature page (MyImpactPage). §7.7 AMENDS the §7.6
 *     "the card is its only front door" rule: the card remains the only
 *     IN-SECTION link to the page, but the section landing now IS the page
 *     (identity-first, per the §7.6 placement rationale). `/memberships/impact`
 *     stays valid and renders exactly this.
 *   PRE-REVEAL (Explorer) → a belonging/memberships overview. NO signature
 *     element of any kind — not the card, not the retired locked slot (§7.6
 *     supersession of 2026-07-09 late stands: the card's first appearance at
 *     promotion must be a true unlock, part of chunk B's reveal choreography).
 *
 * The reveal predicate is chunk B's `showImpactSurfaces`, passed down as
 * `revealed` — never a second predicate (same rule the rail's signature card
 * follows).
 *
 * Why the pre-reveal overview exists at all rather than an empty pane: the rail
 * hides below 2 memberships (§8.10 rule 3), and a brand-new Explorer typically
 * has exactly one (their inviting org or home geo). Without this the section
 * home would render nothing for precisely the users who most need orientation.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useMemberCommunities } from '../../Data/Membership/useMemberCommunities';
import {
  communityInitial,
  type CommunityEntity,
  type CommunityRelationship,
} from '../Communities/communityIdentity';
import { resolveEffectiveLogoUrl } from '../../api/communityMetadata';
import type { SelectableEntry } from '../../Data/Engagement/engagementEntry';
import MyImpactPage from '../MyImpact/MyImpactPage';
import styles from './MembershipsHomePage.module.css';

export interface MembershipsHomePageProps {
  /** Filtered project data rows — forwarded to MyImpactPage post-reveal. */
  data: any[];
  currentUserGuid: string;
  debugMode?: boolean;
  /** Chunk B's reveal predicate (App.showImpactSurfaces). */
  revealed: boolean;
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
  /** The shared composer's engagement entries (roles + project roles). */
  engagementEntries?: SelectableEntry[];
}

export default function MembershipsHomePage({
  data,
  currentUserGuid,
  debugMode = false,
  revealed,
  entities,
  relationships,
  engagementEntries,
}: MembershipsHomePageProps) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  // Same set the rail reads (shared hook — DEV-1458). Computed before the
  // early return so hooks run unconditionally.
  const memberCommunities = useMemberCommunities({
    entities,
    relationships,
    currentUserGuid,
    enabled: !revealed,
  });

  const live = useMemo(
    () => (engagementEntries || []).filter((e) => !e.isPrevious),
    [engagementEntries],
  );
  const roleCount = useMemo(
    () =>
      live.filter(
        (e) => e.typeTag !== 'full-project' && e.typeTag !== 'mini-project',
      ).length,
    [live],
  );
  const projectCount = useMemo(
    () =>
      live.filter(
        (e) => e.typeTag === 'full-project' || e.typeTag === 'mini-project',
      ).length,
    [live],
  );

  // ── POST-REVEAL: the section landing IS the signature page (§7.7 ruling 4).
  if (revealed) {
    return (
      <MyImpactPage data={data} currentUserGuid={currentUserGuid} debugMode={debugMode} />
    );
  }

  // ── PRE-REVEAL (Explorer): belonging overview. No signature element.
  const sorted = [...memberCommunities].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || '')),
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          {t('membershipsHome.title', 'Where you belong')}
        </h1>
        <p className={styles.subtitle}>
          {t(
            'membershipsHome.subtitle',
            'Your communities, roles, and projects live here as they grow.',
          )}
        </p>
      </header>

      {sorted.length === 0 ? (
        <div className={styles.empty}>
          {t(
            'membershipsHome.empty',
            "You don't belong to any communities yet. An invitation will bring you into your first one.",
          )}
        </div>
      ) : (
        <ul className={styles.list}>
          {sorted.map((c) => {
            const logoUrl = resolveEffectiveLogoUrl(c.entity_guid, entities, relationships);
            const name = c.name || t('membershipsHome.unnamed', '(unnamed)');
            return (
              <li key={c.entity_guid}>
                <button
                  type="button"
                  className={styles.row}
                  // IN-SECTION, same as the rail's rows (Skip 2026-07-13): the
                  // community opens in the main pane and the section chrome
                  // stays. These overview rows ARE the pre-reveal Explorer's
                  // rail — a user with one membership never sees the real rail
                  // (§8.10 rule 3), so they must behave identically.
                  onClick={() => navigate(`/memberships/community/${c.entity_guid}#feed`)}
                  title={name}
                >
                  <span className={styles.avatar} aria-hidden="true">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className={styles.avatarImg} />
                    ) : (
                      <span className={styles.avatarInitial}>{communityInitial(c.name)}</span>
                    )}
                  </span>
                  <span className={styles.rowName}>{name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {(roleCount > 0 || projectCount > 0) && (
        <div className={styles.counts}>
          {roleCount > 0 && (
            <span>
              {t('membershipsHome.roleCount', {
                count: roleCount,
                defaultValue: '{{count}} roles',
              })}
            </span>
          )}
          {roleCount > 0 && projectCount > 0 ? <span aria-hidden="true"> · </span> : null}
          {projectCount > 0 && (
            <span>
              {t('membershipsHome.projectCount', {
                count: projectCount,
                defaultValue: '{{count}} projects',
              })}
            </span>
          )}
        </div>
      )}

      {/* §7.3 — the obligations/belonging split, restated where an Explorer
          will actually be standing. This surface is belonging + reference;
          tasks live in the Action Center. */}
      <div className={styles.acHelper}>
        {t('membershipsHome.tasksHelper', 'Looking for tasks?')}{' '}
        <button
          type="button"
          className={styles.acHelperLink}
          onClick={() => navigate('/actions')}
        >
          {t('membershipsHome.openActionCenter', 'Open Action Center')}
        </button>
      </div>
    </div>
  );
}
